import React, { useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import Text from '../../ui/Text'
import { LogTypes } from '../../common/constants/LogTypes'

export type WidgetDropdownFilterOptionsProps = {
    onChange: (options: Array<LogTypes>) => void
    options: Array<LogTypes>
    visibleOptions: Array<LogTypes>
    defaultOptions: Array<LogTypes>
    className?: ClassName
    style?: React.CSSProperties
}

const NoActionsFoundMessage = styled.div`
    padding: 12px 16px;
`

const Value = styled.div`
    font-size: 14px;
    font-weight: 400;
    padding-bottom: 12px;
    cursor: pointer;
`

const Option = styled.label`
    padding: 14px 16px 0 16px;
    display: block;
    margin: 0;

    &:hover {
        background-color: #dedede;
    }

    &:not(:last-of-type) ${Value} {
        border-bottom: 1px solid #dddddd;
    }
`

const Label = styled.span`
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    display: inline-block;
    padding-left: 20px;
    white-space: nowrap;
`

const Checkbox = styled.input<{ dashed?: boolean }>`
    appearance: none;
    position: relative;

    &:checked:after {
        background-color: #2c78f6;
    }

    &:checked:before {
        border-color: #ffffff;
    }

    &:after {
        content: '';
        position: absolute;
        bottom: 3px;
        left: 0;
        width: 14px;
        height: 14px;
        border: 1px solid #d1d1d1;
        border-radius: 2px;
        background-color: #f8f8f8;
    }

    &:before {
        content: '';
        position: absolute;
        width: 8px;
        height: 5px;
        border-bottom: 2px solid #f8f8f8;
        border-left: ${(props) => (props?.dashed ? 'none' : '2px solid #f8f8f8')};
        z-index: 1;
        left: 3px;
        bottom: 9px;
        transform: ${(props) => (props?.dashed ? 'none' : 'rotate(312deg)')};
    }

    &[type='checkbox']:focus {
        outline: none;
    }
`

export function WidgetDropdownFilterOptions({
    onChange,
    options,
    visibleOptions,
    defaultOptions,
    className,
    style,
}: WidgetDropdownFilterOptionsProps): React.ReactElement {
    const isSomeCheckedOptions = !!options.length
    const isSomeUncheckedOptions = options.length !== defaultOptions.length

    const filteredOptions = useMemo(
        () => defaultOptions.filter((option) => visibleOptions.includes(option)),
        [visibleOptions]
    )

    return (
        <div className={cn(className)} style={style} data-testid="WidgetDropdownFilterOptions">
            {visibleOptions.length === defaultOptions.length ? (
                <Option>
                    <Value>
                        <Checkbox
                            type="checkbox"
                            dashed={isSomeUncheckedOptions && isSomeCheckedOptions}
                            checked={isSomeCheckedOptions}
                            onChange={() => onChange(isSomeUncheckedOptions ? defaultOptions : [])}
                            data-testid="selectAll"
                        />
                        <Label>
                            <Text code="btnSelectAll" />
                        </Label>
                    </Value>
                </Option>
            ) : null}
            {filteredOptions.map((option) => (
                <Option key={option}>
                    <Value>
                        <Checkbox
                            type="checkbox"
                            onChange={(event) =>
                                onChange(
                                    event.target.checked
                                        ? options.concat([option])
                                        : options.filter((optionItem) => optionItem !== option)
                                )
                            }
                            name={LogTypes[option]}
                            checked={options.includes(option)}
                            data-testid={LogTypes[option]}
                        />
                        <Label>{LogTypes[option]}</Label>
                    </Value>
                </Option>
            ))}
            {!visibleOptions.length && !filteredOptions.length ? (
                <NoActionsFoundMessage>
                    <Text code="msgNoActionTypesWereFoundForYourSearch" />
                </NoActionsFoundMessage>
            ) : null}
        </div>
    )
}
